import {
  customRoleColors,
  permissionInfo,
  saveCustomRoleSchema,
  type CustomRole,
  type CustomRoleColor,
  type Permission,
} from '@nexo/types';
import { Button, EmptyState, Field, IconButton, Input, cn, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, AtSign, ChevronRight, Plus, Tags, Trash2, Users } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { errorMessage, validate } from '../../lib/forms';
import { can } from '../../lib/permissions';
import { t } from '../../lib/i18n';
import { keys } from '../../lib/queries';
import { PermissionSwitches } from './PermissionEditor';
import { RoleChip, roleColors } from './roles';

type Editing = { kind: 'everyone' } | { kind: 'role'; role: CustomRole } | { kind: 'new' } | null;

const permissionCount = (list: Permission[]) =>
  list.includes('administrator')
    ? t('Administrador')
    : list.length === 1
      ? t('1 permiso')
      : t('{{count}} permisos', { count: list.length });

/**
 * Roles of the team, Discord style: «Todos» (what every member can do) plus custom roles that add
 * permissions, group people and can be @mentioned. Channels can adjust them (channel settings).
 */
export function RolesManager() {
  const { team, roles } = useTeam();
  const canManage = can(team, 'manage_roles');
  const [editing, setEditing] = useState<Editing>(null);
  const { t } = useTranslation();

  if (editing?.kind === 'everyone') {
    return <EveryoneEditor onBack={() => setEditing(null)} />;
  }
  if (editing) {
    return (
      <RoleEditor
        key={editing.kind === 'role' ? editing.role.id : 'new'}
        role={editing.kind === 'role' ? editing.role : undefined}
        onBack={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted">
        <Trans
          i18nKey="Cada persona tiene los permisos de <strong>Todos</strong> más los de sus roles. En los ajustes de cada canal puedes permitir o denegar permisos a un rol o a una persona. Los administradores lo pueden todo."
          components={{ strong: <span className="font-medium text-text" /> }}
        />
      </p>
      <ul className="flex flex-col divide-y divide-border rounded-xl border border-border">
        <RoleButton
          onClick={() => setEditing({ kind: 'everyone' })}
          title={
            <span className="inline-flex items-center gap-1.5 text-sm font-medium">
              <Users className="size-4 text-muted" /> {t('Todos')}
            </span>
          }
          subtitle={t('Todo el equipo · {{permissions}}', {
            permissions: permissionCount(team.everyonePermissions),
          })}
        />
        {roles.map((role) => (
          <RoleButton
            key={role.id}
            onClick={() => setEditing({ kind: 'role', role })}
            title={<RoleChip role={role} className="text-xs" />}
            subtitle={
              <>
                {role.memberCount === 1
                  ? t('1 persona')
                  : t('{{count}} personas', { count: role.memberCount })}{' '}
                · {permissionCount(role.permissions)}
                {role.mentionable && (
                  <span className="inline-flex items-center gap-0.5">
                    {' '}
                    · <AtSign className="size-3" /> {t('mencionable')}
                  </span>
                )}
              </>
            }
          />
        ))}
      </ul>
      {roles.length === 0 && (
        <EmptyState
          icon={<Tags className="size-5" />}
          title={t('Sin roles todavía')}
          description={
            canManage
              ? t('Crea roles como «Diseño» o «Moderación» y dales permisos.')
              : t('Quien pueda gestionar roles podrá crearlos.')
          }
        />
      )}
      {canManage && (
        <Button
          variant="secondary"
          icon={<Plus className="size-4" />}
          className="self-start"
          onClick={() => setEditing({ kind: 'new' })}
        >
          {t('Crear rol')}
        </Button>
      )}
    </div>
  );
}

function RoleButton({
  title,
  subtitle,
  onClick,
}: {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-2"
      >
        <span className="min-w-0 flex-1">
          {title}
          <span className="mt-1 flex items-center gap-1 text-xs text-muted">{subtitle}</span>
        </span>
        <ChevronRight className="size-4 text-subtle" />
      </button>
    </li>
  );
}

function EditorHeader({ title, onBack }: { title: React.ReactNode; onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex items-center gap-2">
      <IconButton label={t('Volver a los roles')} size="sm" onClick={onBack}>
        <ArrowLeft className="size-4" />
      </IconButton>
      <h3 className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</h3>
    </div>
  );
}

/** Permissions of the base role «Todos». */
function EveryoneEditor({ onBack }: { onBack: () => void }) {
  const { team } = useTeam();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canManage = can(team, 'manage_roles');
  const [value, setValue] = useState<Permission[]>(team.everyonePermissions);
  const { t } = useTranslation();
  const save = useMutation({
    mutationFn: () => api.roles.setEveryone(team.id, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.teams });
      toast({ title: t('Permisos de «Todos» guardados'), tone: 'success' });
      onBack();
    },
    onError: (err) =>
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' }),
  });
  return (
    <div>
      <EditorHeader title={t('Todos')} onBack={onBack} />
      <p className="mb-4 text-sm text-muted">
        {t('Lo que puede hacer cualquier persona del equipo, tenga o no otros roles.')}
      </p>
      <PermissionSwitches
        value={value}
        onChange={setValue}
        grantable={team.permissions}
        disabled={!canManage}
      />
      {canManage && (
        <div className="sticky bottom-0 mt-4 flex justify-end gap-2 border-t border-border bg-surface pt-3">
          <Button variant="ghost" onClick={onBack}>
            {t('Cancelar')}
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            {t('Guardar')}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Name, colour, mention and permissions of a role (or a new one). */
function RoleEditor({ role, onBack }: { role?: CustomRole; onBack: () => void }) {
  const { team } = useTeam();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canManage = can(team, 'manage_roles');
  const [name, setName] = useState(role?.name ?? '');
  const [color, setColor] = useState<CustomRoleColor>(role?.color ?? 'indigo');
  const [mentionable, setMentionable] = useState(role?.mentionable ?? true);
  const [perms, setPerms] = useState<Permission[]>(role?.permissions ?? []);
  const [error, setError] = useState<string>();
  const { t } = useTranslation();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.roles(team.id) });
    void queryClient.invalidateQueries({ queryKey: keys.members(team.id) });
  };
  const onError = (err: unknown) =>
    toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' });
  const save = useMutation({
    mutationFn: () => {
      const input = { name, color, mentionable, permissions: perms };
      return role ? api.roles.update(role.id, input) : api.roles.create(team.id, input);
    },
    onSuccess: () => {
      refresh();
      onBack();
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => (role ? api.roles.remove(role.id) : Promise.resolve(undefined)),
    onSuccess: () => {
      refresh();
      onBack();
    },
    onError,
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { errors } = validate(saveCustomRoleSchema, { name, color, mentionable });
    if (errors) return setError(errors.name);
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <EditorHeader
        title={role ? t('Rol «{{name}}»', { name: role.name }) : t('Nuevo rol')}
        onBack={onBack}
      />
      <fieldset disabled={!canManage} className="flex flex-col gap-3">
        <Field label={t('Nombre')} error={error}>
          {(props) => (
            <Input
              {...props}
              placeholder={t('Diseño, Moderación, Soporte…')}
              maxLength={30}
              value={name}
              autoFocus={!role}
              onChange={(e) => {
                setName(e.target.value);
                setError(undefined);
              }}
            />
          )}
        </Field>
        <div role="radiogroup" aria-label={t('Color')} className="flex flex-wrap gap-1.5">
          {customRoleColors.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={color === c}
              aria-label={c}
              onClick={() => setColor(c)}
              className={cn(
                'size-6 rounded-full ring-offset-2 ring-offset-surface transition',
                roleColors[c].dot,
                color === c ? 'ring-2 ring-text' : 'hover:scale-110',
              )}
            />
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={mentionable}
            onChange={(e) => setMentionable(e.target.checked)}
            className="accent-[var(--color-accent)]"
          />
          {t('Se puede mencionar con @{{name}}', { name: name.trim() || t('rol') })}
        </label>
      </fieldset>

      <h3 className="mt-6 mb-2 text-sm font-semibold">{t('Permisos')}</h3>
      {perms.includes('administrator') && (
        <p className="mb-3 rounded-lg bg-danger/10 px-3 py-2 text-xs text-danger">
          {t(permissionInfo.administrator.description)}
        </p>
      )}
      <PermissionSwitches
        value={perms}
        onChange={setPerms}
        grantable={team.permissions}
        disabled={!canManage}
      />

      {canManage && (
        <div className="sticky bottom-0 mt-4 flex items-center gap-2 border-t border-border bg-surface pt-3">
          {role && (
            <Button
              variant="ghost"
              icon={<Trash2 className="size-4" />}
              loading={remove.isPending}
              className="text-danger"
              onClick={() => {
                if (
                  window.confirm(
                    t('¿Borrar el rol «{{name}}»? Se quitará a todos sus miembros.', {
                      name: role.name,
                    }),
                  )
                )
                  remove.mutate();
              }}
            >
              {t('Borrar')}
            </Button>
          )}
          <span className="flex-1" />
          <Button variant="ghost" onClick={onBack}>
            {t('Cancelar')}
          </Button>
          <Button type="submit" loading={save.isPending}>
            {role ? t('Guardar') : t('Crear rol')}
          </Button>
        </div>
      )}
    </form>
  );
}
